import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { format, addDays, subDays } from 'date-fns';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ghlApi } from '../../lib/ghlApi';
import { toast } from 'sonner';
import { AddAppointmentDialog } from '../scheduling/AddAppointmentDialog';
import { AppointmentDetailsDialog } from '../scheduling/AppointmentDetailsDialog';
import { PostBookingModal } from '../scheduling/PostBookingModal';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  Loader2, X, ChevronLeft, ChevronRight,
  Filter, Plus, Check, RefreshCw, Calendar as CalendarIcon,
} from 'lucide-react';

const CALENDAR_COLORS = [
  '#4f46e5', '#0ea5e9', '#10b981', '#f97316',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b',
];

const ROW_HEIGHT = 36;
const COL_WIDTH = 240;
const TIME_COL_W = 64;

const timeToMinutes = (timeStr) => {
  const m = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return 0;
  let h = parseInt(m[1]);
  const min = parseInt(m[2]);
  const p = m[3].toUpperCase();
  if (p === 'PM' && h !== 12) h += 12;
  if (p === 'AM' && h === 12) h = 0;
  return h * 60 + min;
};

const generateTimeSlots = (interval = 15) => {
  const slots = [];
  const baseDate = new Date();
  baseDate.setHours(0, 0, 0, 0);
  for (let hour = 7; hour <= 21; hour++) {
    const slotsPerHour = 60 / interval;
    for (let i = 0; i < slotsPerHour; i++) {
      const slotTime = new Date(baseDate);
      slotTime.setHours(hour, i * interval);
      slots.push(format(slotTime, 'h:mm a'));
    }
  }
  return slots;
};

// Source-based color scheme (module-level for CalendarColumn access)
const SOURCE_COLORS = { crm: '#8b5cf6', juvonno: '#3b82f6', block: '#1e293b' };

// Compute overlap columns for a list of appointments
function computeOverlapLayout(apts) {
  if (!apts.length) return {};
  const layout = {};
  for (const apt of apts) {
    const start = timeToMinutes(apt.time);
    const end = start + parseInt(apt.duration || 30);
    const overlapping = apts.filter(other => {
      const oStart = timeToMinutes(other.time);
      const oEnd = oStart + parseInt(other.duration || 30);
      return start < oEnd && oStart < end;
    });
    overlapping.sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time) || (a.id || '').localeCompare(b.id || ''));
    const myIndex = overlapping.findIndex(a => a.id === apt.id);
    layout[apt.id] = { colIndex: myIndex, totalCols: overlapping.length };
  }
  return layout;
}

// Memoized slot generator cache
const slotCache = {};
function getCachedTimeSlots(interval) {
  if (!slotCache[interval]) slotCache[interval] = generateTimeSlots(interval);
  return slotCache[interval];
}

export function SchedulingGridDialog({
  open, onOpenChange, locationId, initialContact,
  calendars: parentCalendars, appointmentTypes: parentAppointmentTypes,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendars, setCalendars] = useState([]);
  const [visibleCalendarIds, setVisibleCalendarIds] = useState([]);
  const [appointments, setAppointments] = useState({});
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedCalendarName, setSelectedCalendarName] = useState('');
  const [showPostBooking, setShowPostBooking] = useState(false);
  const [postBookingData, setPostBookingData] = useState(null);
  const [showJuvonno, setShowJuvonno] = useState(true);
  const [icsEvents, setIcsEvents] = useState({});
  const [crmBookedIds, setCrmBookedIds] = useState(new Set());
  const lastLoadedRef = useRef('');
  const scrollRef = useRef(null);
  const { user } = useAuth();

  // Source-based color scheme:
  // Purple = CRM-booked (through our scheduling app)
  // Source-based coloring uses module-level SOURCE_COLORS

  const getAptColor = useCallback((aptType, apt) => {
    const type = (aptType || '').toLowerCase();
    const name = (apt?.name || '').toLowerCase();
    const status = (apt?.status || '').toLowerCase();
    // Blocks — check type AND name
    if (type.includes('block') || name.includes('block') || status === 'blocked') return SOURCE_COLORS.block;
    // CRM-booked (our scheduling app) — ID is in our booking logs
    if (crmBookedIds.has(apt?.id)) return SOURCE_COLORS.crm;
    // Juvonno ICS source
    if (apt?.source === 'juvonno_ics' || apt?.source === 'juvonno') return SOURCE_COLORS.juvonno;
    // GHL appointments not in our booking logs = synced from Juvonno/external
    return SOURCE_COLORS.juvonno;
  }, [crmBookedIds]);

  // Initialize
  useEffect(() => {
    if (!open || !locationId) return;
    if (parentCalendars?.length) {
      // Defense-in-depth: drop any calendars that have been offboarded since the
      // parent component fetched its list. Backend filters too, but the parent's
      // state can be stale if the user kept the page open during an offboard.
      const cals = parentCalendars
        .filter(c => c.isActive !== false)
        .map((cal, idx) => ({
          ...cal,
          color: cal.color || CALENDAR_COLORS[idx % CALENDAR_COLORS.length],
          visible: true,
        }));
      setCalendars(cals);
      setVisibleCalendarIds(cals.map(c => c.id));
    }
    if (parentAppointmentTypes?.length) {
      setAppointmentTypes(parentAppointmentTypes);
    } else {
      ghlApi.getAppointmentTypes(locationId).then(r => setAppointmentTypes(r.data || [])).catch(() => {});
    }
  }, [open, locationId, parentCalendars, parentAppointmentTypes]);

  // Reset state only when dialog OPENS (not on every initialContact reference change)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Dialog just opened — reset everything to today
      const today = new Date();
      lastLoadedRef.current = '';
      setCurrentDate(today);
      setAppointments({});
      setAvailability({});
      setShowAddDialog(false);
      setBookingPrefill(null);
      setSelectedAppointment(null);
      setShowFilters(false);
      setShowPostBooking(false);
      setPostBookingData(null);
      setShowJuvonno(false);
      setIcsEvents({});
    }
    prevOpenRef.current = open;
  }, [open]);

  // Load data using bulk-day endpoint
  const loadData = useCallback(async (forceRefresh = false) => {
    if (!locationId || !open) return;
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const cacheKey = `${locationId}-${dateStr}`;
    if (!forceRefresh && lastLoadedRef.current === cacheKey) return;
    lastLoadedRef.current = cacheKey;

    setLoading(true);
    try {
      const resp = await ghlApi.getCalendarBulkDay(locationId, dateStr, 'America/Toronto', forceRefresh);
      const data = resp.data;

      if (!parentCalendars?.length && data.calendars?.length) {
        const sorted = [...data.calendars]
          .filter(c => c.isActive !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        const cals = sorted.map((cal, idx) => ({
          ...cal,
          color: CALENDAR_COLORS[idx % CALENDAR_COLORS.length],
          visible: true,
        }));
        setCalendars(cals);
        setVisibleCalendarIds(cals.map(c => c.id));
      }

      const apts = {};
      for (const [calId, events] of Object.entries(data.appointments || {})) {
        apts[calId] = (events || [])
          .filter(ev => ev.appointmentStatus)
          .map(ev => {
            const s = new Date(ev.startTime);
            const e = ev.endTime ? new Date(ev.endTime) : new Date(s.getTime() + 30 * 60000);
            let rawName = (ev.title || ev.contactName || 'Unnamed').split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
            // Clean: remove "| in clinic" and type prefixes like "PT - ", "Mva - ", "Pt Assessment - "
            rawName = rawName.replace(/\s*\|\s*in clinic/gi, '').trim();
            rawName = rawName.replace(/^(Pt Assessment|Pt|Mva|Mva-a|Follow-up|Follow Up)\s*[-–]\s*/i, '').trim();
            return {
              id: ev.id, name: rawName,
              time: format(s, 'h:mm a'), date: dateStr,
              status: ev.appointmentStatus === 'cancelled' ? 'cancelled' : ev.appointmentStatus === 'confirmed' ? 'confirmed' : 'scheduled',
              type: ev.appointmentType || ev.title || 'Appointment',
              duration: String(Math.round((e - s) / 60000)),
              notes: ev.notes, contactId: ev.contactId, source: 'ghl',
            };
          });
      }
      setAppointments(apts);

      const avail = {};
      for (const [calId, slotData] of Object.entries(data.availability || {})) {
        const daySlots = slotData?.[dateStr]?.slots || slotData?.[dateStr] || [];
        avail[calId] = Array.isArray(daySlots) ? daySlots.map(t => format(new Date(t), 'h:mm a')) : [];
      }
      setAvailability(avail);

      // Load ICS events for this date
      try {
        const icsResp = await ghlApi.getIcsEvents(locationId, dateStr);
        setIcsEvents(icsResp.data?.events || {});
      } catch { setIcsEvents({}); }

      // Load CRM-booked appointment IDs for this date
      try {
        const crmResp = await ghlApi.getCrmBookedIds(locationId, dateStr);
        setCrmBookedIds(new Set(crmResp.data?.ids || []));
      } catch { setCrmBookedIds(new Set()); }
    } catch {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [locationId, currentDate, open, parentCalendars]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDateChange = (date) => {
    lastLoadedRef.current = '';
    setAppointments({});
    setAvailability({});
    setCurrentDate(date);
  };

  const [hideNonWorking, setHideNonWorking] = useState(true);

  const allFilteredCalendars = calendars.filter(c => visibleCalendarIds.includes(c.id));
  const filteredCalendars = allFilteredCalendars.filter(cal => {
    if (!hideNonWorking) return true;
    const calAvail = availability[cal.id] || [];
    const calApts = appointments[cal.id] || [];
    return calAvail.length > 0 || calApts.length > 0;
  });
  const hiddenCount = allFilteredCalendars.length - filteredCalendars.length;
  const isToday = format(currentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  const nowMinutes = isToday ? new Date().getHours() * 60 + new Date().getMinutes() : -1;

  // Merge ICS events into appointments when showJuvonno is true
  const mergedAppointments = {};
  for (const calId of Object.keys(appointments)) {
    mergedAppointments[calId] = [...(appointments[calId] || [])];
    if (showJuvonno && icsEvents[calId]) {
      mergedAppointments[calId].push(...icsEvents[calId].map(e => ({
        ...e,
        name: (e.name || '').replace(/\s*\|\s*in clinic/gi, '').replace(/^(Pt Assessment|Pt|Mva|Mva-a|Follow-up|Follow Up)\s*[-–]\s*/i, '').trim(),
        source: e.source || 'juvonno_ics',
      })));
    }
  }
  if (showJuvonno) {
    for (const calId of Object.keys(icsEvents)) {
      if (!mergedAppointments[calId]) {
        mergedAppointments[calId] = icsEvents[calId].map(e => ({
          ...e,
          name: (e.name || '').replace(/\s*\|\s*in clinic/gi, '').replace(/^(Pt Assessment|Pt|Mva|Mva-a|Follow-up|Follow Up)\s*[-–]\s*/i, '').trim(),
          source: e.source || 'juvonno_ics',
        }));
      }
    }
  }

  const contactName = initialContact
    ? (`${initialContact.firstName || ''} ${initialContact.lastName || ''}`.trim() || '').split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[96vw] w-[96vw] max-h-[92vh] h-[92vh] p-0 overflow-hidden flex flex-col [&>button.absolute]:hidden" data-testid="scheduling-grid-dialog">
        <DialogTitle className="sr-only">Schedule Appointment</DialogTitle>

        {/* TOOLBAR */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-white flex-shrink-0">
          {/* Customer name + close */}
          {contactName && (
            <div className="flex items-center gap-2 mr-3 pr-3 border-r">
              <div className="h-7 w-7 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                {contactName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <span className="text-sm font-bold text-slate-900" data-testid="schedule-contact-name">{contactName}</span>
            </div>
          )}

          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleDateChange(subDays(currentDate, 1))} data-testid="prev-day-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 gap-2 px-3 font-semibold text-sm" data-testid="date-picker-btn">
                <CalendarIcon className="h-4 w-4 text-slate-500" />
                {format(currentDate, 'EEE, MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={currentDate} onSelect={d => d && handleDateChange(d)} initialFocus />
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleDateChange(addDays(currentDate, 1))} data-testid="next-day-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs px-3 ml-1" onClick={() => handleDateChange(new Date())} data-testid="today-btn">
            <RefreshCw className="h-3 w-3" /> Today
          </Button>

          <div className="flex-1" />

          <Button variant={showFilters ? 'default' : 'outline'} size="icon" className="h-8 w-8 rounded-full" onClick={() => setShowFilters(p => !p)} data-testid="filter-toggle-btn">
            <Filter className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => loadData(true)} disabled={loading} title="Refresh" data-testid="refresh-grid-btn">
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
          <Button size="sm" className="h-8 gap-1.5 text-xs rounded-full px-4" onClick={() => { setBookingPrefill(null); setShowAddDialog(true); }} data-testid="add-appointment-btn">
            <Plus className="h-3.5 w-3.5" /> Book
          </Button>
          <button onClick={() => onOpenChange(false)} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors" data-testid="close-schedule-dialog">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* FILTER BAR */}
        {showFilters && calendars.length > 0 && (
          <div className="flex gap-1.5 px-4 py-2 bg-slate-50 border-b overflow-x-auto flex-shrink-0" data-testid="calendar-filter-panel">
            {calendars.map(cal => (
              <button key={cal.id}
                onClick={() => setVisibleCalendarIds(prev => prev.includes(cal.id) ? prev.filter(id => id !== cal.id) : [...prev, cal.id])}
                className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap',
                  visibleCalendarIds.includes(cal.id) ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-400'
                )}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: cal.color }} />
                {cal.name}
              </button>
            ))}
          </div>
        )}

        {/* Color Legend */}
        <div className="flex items-center gap-4 px-4 py-1.5 bg-slate-50/80 border-b text-[10px] font-medium text-slate-600 shrink-0">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SOURCE_COLORS.crm }} />CRM Booked</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SOURCE_COLORS.juvonno }} />Juvonno</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SOURCE_COLORS.block }} />Block</span>
          {hiddenCount > 0 && (
            <button onClick={() => setHideNonWorking(!hideNonWorking)} className="ml-auto text-[10px] font-medium text-indigo-600 hover:text-indigo-800">
              {hideNonWorking ? `+${hiddenCount} not working` : 'Hide non-working'}
            </button>
          )}
        </div>

        {/* GRID */}
        {loading && Object.keys(appointments).length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
            <span className="ml-3 text-slate-500 text-sm">Loading calendars...</span>
          </div>
        ) : filteredCalendars.length === 0 && hiddenCount > 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <p className="text-slate-400 text-sm">No practitioners working on this day</p>
            <button onClick={() => setHideNonWorking(false)} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
              Show all {hiddenCount} practitioners
            </button>
          </div>
        ) : filteredCalendars.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
            No calendars visible. Use the filter to show calendars.
          </div>
        ) : (
          <div className="flex-1 overflow-auto min-h-0" ref={scrollRef}>
            <div className="flex">
              {filteredCalendars.map(cal => (
                <CalendarColumn
                  key={cal.id}
                  cal={cal}
                  appointments={mergedAppointments[cal.id] || []}
                  availability={availability[cal.id] || []}
                  isToday={isToday}
                  nowMinutes={nowMinutes}
                  currentDate={currentDate}
                  getAptColor={getAptColor}
                  onSlotClick={(timeSlot) => onSlotClick(cal.id, timeSlot, format(currentDate, 'yyyy-MM-dd'))}
                  onAptClick={(apt) => { setSelectedAppointment(apt); setSelectedCalendarName(cal.name); }}
                  crmBookedIds={crmBookedIds}
                />
              ))}
            </div>
          </div>
        )}

        <AddAppointmentDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          calendars={calendars}
          onSubmit={handleAddAppointment}
          prefill={bookingPrefill}
          locationId={locationId}
          appointmentTypes={appointmentTypes}
          initialContact={initialContact}
          onBooked={(data) => {
            setPostBookingData(data);
            setShowPostBooking(true);
          }}
        />
        <PostBookingModal
          open={showPostBooking}
          onOpenChange={setShowPostBooking}
          bookingData={postBookingData}
          locationId={locationId}
        />
        <AppointmentDetailsDialog
          open={!!selectedAppointment}
          onOpenChange={o => !o && setSelectedAppointment(null)}
          appointment={selectedAppointment}
          calendarName={selectedCalendarName}
        />
      </DialogContent>
    </Dialog>
  );

  function onSlotClick(calendarId, timeSlot, dateStr) {
    const cal = calendars.find(c => c.id === calendarId);
    const slotDuration = cal?.slotDuration || cal?.slotInterval || 30;
    setBookingPrefill({
      date: dateStr, time: timeSlot, calendarId,
      calendarName: cal?.name || '',
      duration: String(slotDuration),
    });
    setShowAddDialog(true);
  }

  async function handleAddAppointment(formData) {
    const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
    const durationMinutes = parseInt(formData.duration);
    const endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60000);
    const userName = user?.name || user?.email || 'System';
    const resp = await ghlApi.createCalendarAppointment(locationId, {
      calendar_id: formData.calendarId,
      contact_id: formData.contactId,
      start_time: startDateTime.toISOString(),
      end_time: endDateTime.toISOString(),
      title: formData.title || formData.appointmentType || 'Appointment',
      appointment_status: 'confirmed',
      user_name: userName,
      ignore_free_slot_validation: formData.ignoreFreeSlotValidation || false,
    });
    toast.success('Appointment booked!');
    lastLoadedRef.current = '';
    loadData(true);
    return resp;
  }
}

// Per-PT Calendar Column Component
function CalendarColumn({ cal, appointments: calApts, availability: calAvail, isToday, nowMinutes, currentDate, getAptColor, onSlotClick, onAptClick, crmBookedIds }) {
  const slotDuration = cal.slotDuration || cal.slotInterval || 30;
  const calTimeSlots = getCachedTimeSlots(slotDuration);
  const gridStartMin = timeToMinutes(calTimeSlots[0]);
  const overlapLayout = useMemo(() => computeOverlapLayout(calApts), [calApts]);
  const contentWidth = COL_WIDTH - TIME_COL_W;

  return (
    <div className="flex-shrink-0 border-r bg-white" style={{ width: COL_WIDTH }} data-testid={`cal-column-${cal.id}`}>
      {/* Sticky column header */}
      <div className="sticky top-0 z-10 bg-white border-b">
        <div className="px-2.5 py-2 border-b" data-testid={`cal-header-${cal.id}`}>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color }} />
            <span className="text-xs font-bold text-slate-800 truncate">{cal.name}</span>
          </div>
          <div className="flex gap-1.5 mt-1">
            {calApts.length > 0 && <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-px rounded-full font-semibold">Booked</span>}
            {calAvail.length > 0 && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-px rounded-full font-semibold">+ {calAvail.length} free</span>}
            {calApts.length === 0 && calAvail.length === 0 && <span className="text-[9px] text-slate-400">No data</span>}
          </div>
        </div>
        {/* Sub-header */}
        <div className="flex text-[9px] font-semibold text-slate-500 uppercase tracking-wide">
          <span className="w-16 px-1.5 py-1 border-r text-center">Time</span>
          <span className="flex-1 px-1.5 py-1">Availability</span>
        </div>
      </div>

      {/* Time rows + appointment overlay */}
      <div className="relative">
        {calTimeSlots.map(slot => {
          const slotMin = timeToMinutes(slot);
          const isAvail = calAvail.includes(slot);
          const hasCovering = calApts.some(a => {
            const aMin = timeToMinutes(a.time);
            return aMin <= slotMin && aMin + parseInt(a.duration || 30) > slotMin;
          });
          const isNow = isToday && Math.abs(slotMin - nowMinutes) < slotDuration / 2;
          const isPast = isToday && slotMin < nowMinutes - slotDuration;

          return (
            <div key={slot} className={cn('flex border-b relative', isPast && 'bg-slate-50/60', isNow && 'bg-blue-50/40')}
              style={{ height: ROW_HEIGHT }}
              data-minutes={slotMin}
            >
              {isNow && (
                <div className="absolute inset-x-0 top-0 z-20 flex items-center pointer-events-none">
                  <div className="flex-1 h-px bg-blue-500" />
                </div>
              )}
              <div className={cn("w-16 flex-shrink-0 border-r text-[10px] font-medium text-right pr-2 pt-2", (isAvail || hasCovering) ? "text-slate-800 font-bold" : "text-slate-300")}>
                {slot}
              </div>
              <div
                className={cn('flex-1 px-1 flex items-center',
                  isAvail && !hasCovering && 'cursor-pointer hover:bg-emerald-50'
                )}
                onClick={() => isAvail && !hasCovering && onSlotClick(slot)}
              >
                {!hasCovering && isAvail && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-white font-bold bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded shadow-sm transition-colors">
                    <Plus className="h-3 w-3" /> Book
                  </span>
                )}
                {!hasCovering && !isAvail && (
                  <span className="text-[10px] text-slate-300">Unavailable</span>
                )}
              </div>
            </div>
          );
        })}

        {/* Appointment overlays */}
        {calApts.map(apt => {
          const startMin = timeToMinutes(apt.time);
          const slotOffset = (startMin - gridStartMin) / slotDuration;
          const durationMin = parseInt(apt.duration || 30);
          const durationSlots = Math.max(1, Math.ceil(durationMin / slotDuration));
          const top = slotOffset * ROW_HEIGHT + 1;
          const height = durationSlots * ROW_HEIGHT - 2;
          const color = getAptColor(apt.type, apt);
          const isBlock = color === SOURCE_COLORS.block;
          const isCrmBooked = crmBookedIds.has(apt.id);
          const ol = overlapLayout[apt.id] || { colIndex: 0, totalCols: 1 };
          const slotWidth = (contentWidth - 4) / ol.totalCols;
          const left = TIME_COL_W + 2 + ol.colIndex * slotWidth;

          if (slotOffset < 0 || slotOffset >= calTimeSlots.length) return null;

          return (
            <button key={apt.id}
              onClick={(e) => { e.stopPropagation(); onAptClick(apt); }}
              style={{
                position: 'absolute', top, height,
                left, width: slotWidth - 2,
                backgroundColor: isBlock ? '#1e293b' : color + '20',
                borderColor: color + '50',
                borderLeftWidth: 3,
                borderLeftColor: color,
                pointerEvents: 'auto',
              }}
              className={cn(
                'rounded border text-left px-1.5 py-0.5 hover:shadow-md transition-shadow cursor-pointer overflow-hidden z-10',
                apt.status === 'cancelled' && 'opacity-50'
              )}
              data-testid={`appointment-${apt.id}`}
            >
              <span className={cn("text-[10px] font-semibold truncate flex items-center gap-0.5", isBlock ? "text-white" : "text-slate-800")}>
                {apt.name}
                {apt.status === 'confirmed' && !isBlock && <Check className="h-2.5 w-2.5 text-green-600 flex-shrink-0" />}
                {apt.status === 'cancelled' && <X className="h-2.5 w-2.5 text-red-500 flex-shrink-0" />}
              </span>
              {durationSlots >= 2 && (
                <>
                  <span className={cn("text-[8px] truncate block", isBlock ? "text-slate-300" : "text-slate-500")}>{apt.time} · {durationMin}min</span>
                  {isCrmBooked && (
                    <span className="inline-block text-[8px] mt-0.5 px-1 py-px rounded-sm font-semibold text-white truncate"
                      style={{ backgroundColor: color }}>{apt.type}</span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
